<template>
  <!--
    整个组件挂 @click.stop：卡片外层（ArticleItem / MobileFeedItem）在 @click.native 上
    绑了「打开帖子详情」，不拦住冒泡的话点选项会直接跳走，投不了票。
  -->
  <div class="poll-view" @click.stop>
    <div class="poll-view__title">
      <span class="poll-view__title-text">{{ poll.title }}</span>
      <span v-if="isMultiple" class="poll-view__tag">[多选]</span>
    </div>

    <div
      v-for="option in poll.options"
      :key="option.id"
      class="poll-view__option"
      :class="{
        'is-result': showResult,
        'is-chosen': !!option.isVoted,
        'is-pending': isPending(option),
        'is-clickable': interactive
      }"
      @click.stop="onOptionClick(option)"
    >
      <!-- 百分比条垫在文字下面，宽度由后端算好的 percent 驱动 -->
      <span
        v-if="showResult"
        class="poll-view__bar"
        :style="{ width: barWidth(option) }"
      ></span>
      <span class="poll-view__option-body">
        <!--
          只要还能投，就必须给出可勾选的标记。条件必须是 interactive 而不是
          !showResult && interactive —— 后者漏掉了「能看到结果、但自己还没投」
          这种组合（作者看自己的投票，或作者开了「允许直接查看结果」），
          会导致点了选项完全没反应。
        -->
        <span
          v-if="interactive"
          class="poll-view__check"
          :class="{ 'is-square': isMultiple, 'is-active': isPending(option) }"
        >
          <i v-if="isPending(option)" class="el-icon-check"></i>
        </span>
        <span class="poll-view__option-main">
          <span class="poll-view__option-text">{{ option.content }}</span>
          <!-- 自己投过的那项在文字后面跟一个勾，和文字同色 -->
          <i v-if="showResult && option.isVoted" class="el-icon-check poll-view__chosen-mark"></i>
        </span>
        <span v-if="showResult" class="poll-view__count">{{ formatVotes(option) }}</span>
      </span>
    </div>

    <!-- 多选需要一个确认入口；单选点一下即投 -->
    <div v-if="canSubmitMultiple" class="poll-view__submit">
      <el-button
        type="primary"
        size="mini"
        :loading="voting"
        :disabled="pendingIds.length === 0"
        @click.stop="submitVote"
      >
        提交投票
      </el-button>
    </div>

    <div class="poll-view__footer">
      <svg-icon icon-class="publish-vote" size="12" class="poll-view__footer-icon" />
      <span>{{ footerText }}</span>
    </div>
  </div>
</template>

<script>
import { articleFetch } from '@/api'
import { getToken } from '@/utils/cookieUtil'

// 认证 / 封禁类错误已由 request.js 的拦截器统一处理（弹登录框、封禁弹窗），
// 业务层再 toast 一次会出现两个提示叠在一起。与 articleMixinx.js 保持同一套约定
const SILENT_ERRORS = ['__SILENT_AUTH__', '__ACCOUNT_BANNED__']

export default {
  name: 'PollView',
  props: {
    // 后端下发的 ArticlePollVO
    poll: {
      type: Object,
      required: true
    },
    // 帖子id（雪花id，务必按字符串处理）
    articleId: {
      type: [String, Number],
      required: true
    }
  },
  data () {
    return {
      // 多选待提交的选项id（字符串），单选不用这个中间态
      pendingIds: [],
      voting: false
    }
  },
  computed: {
    isMultiple () {
      return this.poll.multiple === 1 || this.poll.multiple === true
    },
    hasVoted () {
      return !!this.poll.isVoted
    },
    expired () {
      return !!this.poll.expired
    },
    interactive () {
      return !this.hasVoted && !this.expired
    },
    // 是否展示票数与占比。只认后端给的 resultVisible：
    // 票数保密的判断在服务端做，客户端拿到的已经是 0，不该也不需要自己再算一遍规则
    showResult () {
      return !!this.poll.resultVisible
    },
    canSubmitMultiple () {
      return this.interactive && this.isMultiple
    },
    footerText () {
      const count = Number(this.poll.voteUserCount) || 0
      const people = count > 0 ? `${this.formatCount(count)}人参与` : '还没有人参与'
      if (this.expired) return `${people} · 投票已结束`
      if (this.hasVoted) return `${people} · 你已投票`
      if (this.isMultiple) return `${people} · 可多选`
      return people
    }
  },
  watch: {
    // 列表复用或投票成功后 poll 换了对象，待提交的中间态必须清掉
    'poll.id' () {
      this.pendingIds = []
      this.voting = false
    }
  },
  methods: {
    // 雪花id超出 JS 安全整数范围，一律按字符串比较
    idOf (option) {
      return String(option.id)
    },
    isPending (option) {
      return this.pendingIds.indexOf(this.idOf(option)) > -1
    },
    formatCount (count) {
      if (count >= 100000000) return (count / 100000000).toFixed(1) + '亿'
      if (count >= 10000) return (count / 10000).toFixed(1) + 'w'
      return String(count)
    },
    // 右侧显示得票数而不是百分比。大票数走 formatCount 收成 "1.2w"，
    // 否则五位数会把选项文字挤窄
    formatVotes (option) {
      return this.formatCount(Number(option.voteCount) || 0)
    },
    barWidth (option) {
      const percent = Number(option.percent) || 0
      if (percent <= 0) return '0%'
      // 条宽直接用「占总票数的比例」：这样条长和右边的票数是同一套刻度，
      // 谁占大头一眼看得出。归一化到最高票会让第一名永远满格，和旁边的数字读起来对不上。
      // 留 2% 保底，只有 1 票时条不至于细到看不见、被当成没渲染
      return `${Math.min(Math.max(percent, 2), 100)}%`
    },
    checkLogin () {
      if (!getToken()) {
        // 主动弹登录框而不是等 401 兜底，否则用户点完要白等一次往返
        this.$store.commit('systemStore/setLoginFlag', true)
        return false
      }
      return true
    },
    onOptionClick (option) {
      if (!this.interactive) {
        if (this.hasVoted) {
          this.$toast.warning('你已经参与过这个投票啦')
        } else if (this.expired) {
          this.$toast.warning('投票已经结束啦')
        }
        return
      }
      if (!this.checkLogin()) return

      const id = this.idOf(option)
      if (!this.isMultiple) {
        // 单选点一下即投。先把选中态点亮再发请求，否则从点击到接口返回
        // 这段时间选项毫无反应，看着像没点上；失败会在 doVote 里收回
        this.pendingIds = [id]
        this.doVote([id])
        return
      }
      const index = this.pendingIds.indexOf(id)
      if (index > -1) {
        this.pendingIds.splice(index, 1)
        return
      }
      const maxChoice = Number(this.poll.maxChoice) || this.poll.options.length
      if (this.pendingIds.length >= maxChoice) {
        this.$toast.warning(`最多只能选择${maxChoice}项`)
        return
      }
      this.pendingIds.push(id)
    },
    submitVote () {
      if (!this.pendingIds.length) {
        this.$toast.warning('请先选择一个选项')
        return
      }
      if (!this.checkLogin()) return
      this.doVote(this.pendingIds.slice())
    },
    async doVote (optionIds) {
      if (this.voting) return
      this.voting = true
      // 单选的选中态是点击时乐观点亮的，失败必须收回，
      // 否则会留下一个「看着投上了其实没投」的假状态。
      // 多选是用户自己勾的，失败保留原样方便直接重试
      const rollbackPending = !this.isMultiple
      try {
        const res = await articleFetch.votePoll({
          articleId: String(this.articleId),
          optionIds
        })
        this.pendingIds = []
        // 直接用接口返回的最新投票数据替换，不在本地估算票数：
        // 别人的票随时在涨，本地 +1 会和真实结果对不上
        this.$emit('voted', res.data)
      } catch (err) {
        if (rollbackPending) this.pendingIds = []
        if (!SILENT_ERRORS.includes(err?.message)) {
          // 业务错误 request.js 已经 toast 过一次，这里只兜网络异常
          console.error('投票失败:', err)
        }
      } finally {
        this.voting = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
/*
  配色：底条一律中性灰，只有「自己投过的那项」用蓝色高亮。
  蓝色刻意不跟 --theme-color 走 —— 主题色浅色是蓝、深色是紫，
  而浅蓝底条必须和蓝字成对出现，跟着主题变会出现紫字配蓝底。
  用自定义属性而不是 scss 变量，是为了深色模式只覆盖两行就够。
*/
.poll-view {
  --poll-accent: #2e7bf6;
  --poll-accent-tint: rgba(46, 123, 246, 0.08);
  --poll-accent-bar: rgba(46, 123, 246, 0.22);
  --poll-bar: rgba(144, 147, 153, 0.18);

  margin: 10px 0 6px;
  padding: 12px;
  border: 1px solid var(--border-line, #f0f0f0);
  border-radius: 8px;
  background-color: var(--background-color, #fff);
}

/* 深色底上 #2e7bf6 对比度不够，提亮一档；灰条也要反过来提亮才看得见 */
html[data-theme='dark'] .poll-view {
  --poll-accent: #5b9cff;
  --poll-accent-tint: rgba(91, 156, 255, 0.12);
  --poll-accent-bar: rgba(91, 156, 255, 0.28);
  --poll-bar: rgba(220, 223, 230, 0.16);
}

.poll-view__title {
  margin-bottom: 10px;
}

.poll-view__title-text {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color1, #303133);
  line-height: 1.5;
}

.poll-view__tag {
  margin-left: 4px;
  font-size: 12px;
  color: #909399;
}

.poll-view__option {
  position: relative;
  overflow: hidden;
  margin-bottom: 8px;
  border: 1px solid var(--border-line, #ebeef5);
  border-radius: 6px;

  &.is-clickable {
    cursor: pointer;

    &:hover {
      border-color: var(--poll-accent);
    }
  }

  /* 出结果后不再描边，信息交给底条表达，描边会和底条打架 */
  &.is-chosen {
    border-color: transparent;
    background-color: var(--poll-accent-tint);
  }

  /* 已勾选、待提交：用描边表示「选上了但还没生效」，
     和 is-chosen（已投出去，纯底色）区分开 */
  &.is-pending {
    border-color: var(--poll-accent);
    background-color: var(--poll-accent-tint);
  }
}

/* 票数条垫在文字下面 */
.poll-view__bar {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background-color: var(--poll-bar);
  transition: width 0.35s ease;
}

.poll-view__option.is-chosen .poll-view__bar {
  background-color: var(--poll-accent-bar);
}

.poll-view__option-body {
  position: relative;
  display: flex;
  align-items: center;
  padding: 10px 12px;
}

.poll-view__check {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-right: 8px;
  border: 1px solid #c0c4cc;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  &.is-square {
    border-radius: 3px;
  }

  &.is-active {
    background-color: var(--poll-accent);
    border-color: var(--poll-accent);
    color: #fff;
  }

  i {
    font-size: 10px;
  }
}

/* 文字和勾占满中间，把票数顶到最右 */
.poll-view__option-main {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
}

.poll-view__option-text {
  font-size: 13px;
  color: var(--text-color1, #303133);
  line-height: 1.4;
  word-break: break-word;
}

.poll-view__chosen-mark {
  flex-shrink: 0;
  margin-left: 4px;
  font-size: 13px;
  font-weight: 700;
  color: var(--poll-accent);
}

.poll-view__count {
  flex-shrink: 0;
  margin-left: 12px;
  font-size: 13px;
  color: #8a9099;
  /* 等宽数字，多行票数的右边缘才对得齐 */
  font-variant-numeric: tabular-nums;
}

.poll-view__option.is-chosen .poll-view__option-text,
.poll-view__option.is-chosen .poll-view__count,
.poll-view__option.is-pending .poll-view__option-text {
  color: var(--poll-accent);
}

.poll-view__option.is-chosen .poll-view__count {
  font-weight: 600;
}

.poll-view__submit {
  margin: 4px 0 10px;
}

.poll-view__footer {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #909399;
}

.poll-view__footer-icon {
  margin-right: 4px;
}

@media screen and (max-width: 767px) {
  .poll-view {
    padding: 10px;
  }

  .poll-view__option-body {
    padding: 9px 10px;
  }
}
</style>
